import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Card,
    Button,
    Badge,
    Spinner,
    Alert,
} from 'react-bootstrap';
import {
    useSearchParams,
    useNavigate,
    useLocation,
    Link,
} from 'react-router-dom';
import { backLinkFor } from './backLink';
import courseService from '@/shared/services/courseService';
import applicationService from '@/shared/services/applicationService';
import GuideLink from '@/components/common/GuideLink';
import ApplicationDeadlineCard from '@/components/common/ApplicationDeadlineCard';
import WizardStepIndicator from '@/components/common/WizardStepIndicator';
import useStepChangeFocus from '@/shared/hooks/useStepChangeFocus';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import useSavedProgress from '@/shared/hooks/useSavedProgress';
import useLeaveGuard from '@/shared/hooks/useLeaveGuard';
import SaveProgressStatus from '@/components/common/SaveProgressStatus';
import { useTerm } from '@/shared/contexts/TermContext';
import { usePartsOfTerm } from '@/shared/hooks/usePartsOfTerm';
import {
    defaultPartOfTerm,
    OTHER_PART_OF_TERM,
} from '@/shared/utils/partsOfTerm';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import ActionBar from '@/components/common/ActionBar';
import programService from '@/shared/services/programService';
import { subjectUnavailableReasons } from '@/shared/utils/programEligibility';
import CrnHolderNotice from './CrnHolderNotice';
import CreateApplicationSection1, {
    CRN_PATTERN,
    getEligibilityCourseErrors,
} from './CreateApplicationSection1';
import { emptyMeeting, meetingTypesOnly } from './meetings';
import { courseChangeLoss } from './courseChange';
import CreateApplicationSection2, {
    getProgramSelectionErrors,
} from './CreateApplicationSection2';
import CreateApplicationSection3, {
    getAcknowledgementContractErrors,
    useLARequirements,
} from './CreateApplicationSection3';
import CreateApplicationSection4, {
    getDefaultCourseContext,
    getCourseContextErrors,
} from './CreateApplicationSection4';
import CreateApplicationSection5, {
    nomineeIdFieldFor,
    getDefaultProgramResponses,
    getProgramQuestionnaireErrors,
} from './CreateApplicationSection5';
import CreateApplicationSection6, {
    getGeneralAcknowledgmentErrors,
} from './CreateApplicationSection6';
import CreateApplicationSection7, {
    getReviewConfirmationErrors,
} from './CreateApplicationSection7';
import {
    getConfirmedAnswers,
    getRelevantNamedStudents,
    getSavedNamedStudents,
    toProgramPreferences,
} from './ApplicationSummary';
import {
    getDuplicateSeed,
    keepEligiblePrograms,
    findCourseForTerm,
} from './duplicateApplication';
import { getResumeSeed, isOwnDraftMatch } from './draftApplication';
import {
    ALREADY_SUBMITTED_LEFT_MESSAGE,
    ALREADY_SUBMITTED_SAVE_NOTE,
    saveAndSubmit,
} from './submitOutcome';

const LEAVE_UNSAVED_PROMPT =
    'Your latest changes could not be saved. Leave anyway and lose them?';
const LEAVE_NO_COURSE_PROMPT =
    "This request can't be saved until its course is chosen, so what you entered will be lost. Leave anyway?";
const NO_COURSE_YET_NOTE =
    'Choose the course in Eligibility & Course first. A request is saved once its course is known.';
const DRAFT_SAVED_MESSAGE =
    'Your draft is saved. Continue it any time from Drafts below or from your dashboard.';

const NOT_SAVED_PREVIEWING_NOTE = "Nothing was saved: you're previewing.";
// The server's rule for a typed 900 number
const NINE_DIGITS = /^\d{9}$/;

// The section number and CRN a catalog record stands for, if it is one
// section's record; a record for the course as a whole has neither
const sectionOfRecord = (record) => ({
    ...(record.crn ? { crn: record.crn } : {}),
    ...(record.section ? { sectionNumber: record.section } : {}),
});

// Another section of the same course in the same term
const isSiblingSection = (current, picked) =>
    !!current &&
    picked.subject === current.subject &&
    picked.courseNumber === current.courseNumber &&
    picked.term === current.term;

const joinWithAnd = (items) =>
    items.length <= 2
        ? items.join(' and ')
        : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;

function CreateApplication() {
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const { userProfile } = useAuth();
    const previewLocked = usePreviewLock();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Read once: the wizard rewrites its own URL as it saves, and that
    // replaces the state the opening page passed
    const [backLink] = useState(() => backLinkFor(location.state));
    const courseId = searchParams.get('course');
    // The sentinel Daniel's "My course isn't listed" links (Start page,
    // Browse Courses) send faculty to — no real course exists yet, so
    // Section 1 collects one through its own unlisted-course form instead
    // of this effect trying to fetch it.
    const isUnlisted = courseId === 'unlisted';
    // A saved draft being resumed (#519). Read once: saving a new draft
    // writes its id into the URL too, and that one must not be reloaded over
    // what is being typed.
    const [resumeId] = useState(() => searchParams.get('draft'));
    // "Duplicate" on the Applications page (#457) sends the id of the
    // application to copy from. Read once for the same reason: the first save
    // replaces it in the URL with the new draft's id, and the copy's rules
    // (keep copied answers on a course change) still apply this session.
    const [duplicateFromId] = useState(() =>
        resumeId ? null : searchParams.get('duplicateFrom')
    );
    // True until the first course loads on a request started from scratch
    const freshStartRef = useRef(!resumeId && !duplicateFromId);
    // Whether this request is taking its course record now: a new request
    // or a duplicate (both created by the first save), or a draft whose
    // course was just changed. A resumed draft keeps the section and CRN
    // it stored, and so does the server.
    const takesRecordRef = useRef(!resumeId);
    // The draft this wizard saves to, once the first save has created it
    const [savedId, setSavedId] = useState(resumeId);
    const savedIdRef = useRef(resumeId);
    const [resumeDraft, setResumeDraft] = useState(null);
    const [resumeError, setResumeError] = useState(null);
    // Seeded from the draft, with its CRN checked
    const [resumeReady, setResumeReady] = useState(!resumeId);
    // Moved to the first section still needing answers
    const [positioned, setPositioned] = useState(!resumeId);
    const [finishLaterNote, setFinishLaterNote] = useState(null);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    // Bumped by "Try again" after the course failed to load
    const [courseAttempt, setCourseAttempt] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Shown next to Submit rather than at the top with `error`, which is
    // for the course failing to load
    const [submitError, setSubmitError] = useState(null);
    // Who holds the CRN, when the server refused the submit for that
    const [submitHolder, setSubmitHolder] = useState(null);
    // Submitted from somewhere else, so this page can't save any more: the
    // save line and the leave prompt would only repeat the refused save
    // under the submit alert (#394)
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const submitErrorRef = useRevealAlert(submitError);
    const [step, setStep] = useState(1);
    const [eligiblePrograms, setEligiblePrograms] = useState(null);
    const [unavailableReasons, setUnavailableReasons] = useState({});
    // People can move between sections in any order (#463), so a section's
    // missing fields are only pointed out once they've left it or tried to
    // submit, not while it's still being filled in the first time. Keyed
    // by label because the LA-only section shifts the step numbers.
    const [leftSections, setLeftSections] = useState(() => new Set());
    // Sections the person has been on, by label for the same reason. A
    // section with nothing missing only earns its check mark once it has been
    // reached, so a section nobody has opened never shows as done.
    const [reachedSections, setReachedSections] = useState(() => new Set());
    const [submitAttempted, setSubmitAttempted] = useState(false);
    // A new object each time, so asking to focus the same field twice
    // still runs the effect below
    const [focusRequest, setFocusRequest] = useState(null);
    // The server's refusal of the LA nominees' 900 numbers: its message and
    // each refused row's position and ID, so a row's message goes once that
    // ID is edited
    const [nomineeIdRefusal, setNomineeIdRefusal] = useState(null);
    // The server's refusal of the part of term, with the answer it refused,
    // so the message goes once the answer changes
    const [partOfTermRefusal, setPartOfTermRefusal] = useState(null);
    useEffect(() => {
        if (focusRequest) {
            document.getElementById(focusRequest.id)?.focus();
        }
    }, [focusRequest]);

    // Section 1: Eligibility and Course
    const [isSubmittedOnBehalf, setIsSubmittedOnBehalf] = useState(false);
    const [courseConfirmation, setCourseConfirmation] = useState({
        crn: '',
        enrollment: '',
        sectionNumber: '',
        // Filled in with the term's default once its list loads
        partOfTermCode: '',
        partOfTermName: '',
        instructorOfRecordName: '',
        instructorOfRecordEmail: '',
        modality: '',
        otherModality: '',
        meetings: [emptyMeeting()],
    });
    // Result of Section 1's live CRN check — lifted here because it decides
    // whether Section 1 is complete, same as the rest of courseConfirmation's
    // validation, and CreateApplicationSection1 reports it up rather than
    // owning the block/unblock decision itself.
    const [crnCheckResult, setCrnCheckResult] = useState(null);
    // Only a submitted claim on the CRN blocks this application. Another of
    // the applicant's own drafts for the same CRN claims nothing (the unique
    // index covers submitted applications only); blocking on it would leave
    // two such drafts each waiting on the other. Submitting one turns its
    // claim into a block for the other.
    const crnBlocksSubmit = (result) =>
        !!result?.exists && !isOwnDraftMatch(result);
    // Whether Section 1's live CRN check has a request in flight for the
    // currently-typed CRN — factored into eligibilityCourseErrors below
    // alongside crnCheckResult so a submit can't slip through during the
    // debounce window before a conflict would have been reported.
    const [crnChecking, setCrnChecking] = useState(false);
    // Section 1 remounts as people move around (#463), and a check started
    // by an earlier mount can answer late. Only the one for the CRN typed
    // now counts.
    const crnRef = useRef('');
    crnRef.current = courseConfirmation.crn ?? '';
    const handleCrnCheckResult = useCallback((result, crn) => {
        if (crn === crnRef.current) setCrnCheckResult(result);
    }, []);
    const handleCrnCheckingChange = useCallback((checking, crn) => {
        if (crn === crnRef.current) setCrnChecking(checking);
    }, []);
    // Whether Section 1's course picker is mid "Change course" (search or
    // unlisted, not yet confirmed via a pick or "Use this course") — while
    // true, `course` here is still the OLD course, so Section 1 can't count
    // as complete on stale course data paired with an abandoned pick.
    const [pickingNewCourse, setPickingNewCourse] = useState(false);
    // Section 1's "My course isn't listed" form, kept here so what's typed
    // survives leaving the section (#463)
    const [unlistedForm, setUnlistedForm] = useState(null);

    // The application being duplicated
    const [duplicateSource, setDuplicateSource] = useState(null);
    const [duplicateError, setDuplicateError] = useState(null);
    // Copied programs taken out because the course can't take them, kept
    // so they come back if the course changes
    const [prunedPrograms, setPrunedPrograms] = useState([]);
    const seededIdRef = useRef(null);

    // Section 2: Program Selection and Preferences
    const [selectedPrograms, setSelectedPrograms] = useState([]);

    // Section 4: Support Rationale
    const [courseContext, setCourseContext] = useState(getDefaultCourseContext);

    // Section 5: Program Questionnaire
    const [namedStudents, setNamedStudents] = useState([]);
    const [programResponses, setProgramResponses] = useState(
        getDefaultProgramResponses
    );

    // Changing course from inside Section 1 goes through the URL rather
    // than local state, so the existing courseId-keyed effect below refetches
    // both `course` and `eligiblePrograms` for free, and a refresh keeps the
    // new choice instead of reverting to the original ?course= value. Also
    // how Section 1's unlisted-course form hands off once it creates (or
    // finds) a real Course — same "now we have a real course id" moment.
    //
    // The section-specific fields (CRN, enrollment, section #, modality,
    // meetings) belong to the course just left, not the one just picked —
    // carrying them over would submit one course's CRN against a different
    // course. isSubmittedOnBehalf/instructor-of-record aren't course-
    // specific (they're about who's asking), so those are left alone. The
    // stale CRN check result goes too, since it answered for a CRN that no
    // longer applies here either way.
    //
    // selectedPrograms is cleared too — a program picked for the old course
    // that the new one isn't eligible for would otherwise stay ranked and
    // submittable (the server doesn't check eligibility either) unless the
    // user noticed and removed it. Clearing it here means eligibility is
    // re-decided from scratch for whatever course is actually being applied
    // for, same reasoning as the courseConfirmation fields above. A
    // duplicate (#457) keeps its copied answers instead, and the effect
    // further down drops programs the new course can't take.
    // The URL for a course choice. Once a draft exists it carries the
    // draft's id, so a reload resumes the draft instead of starting over.
    const wizardParams = (course) => {
        if (savedIdRef.current) return { course, draft: savedIdRef.current };
        if (duplicateFromId) return { course, duplicateFrom: duplicateFromId };
        return { course };
    };

    // What a change would clear, read by the warning in Section 1 and by the
    // clearing in handleCourseChange alike
    const courseLoss = courseChangeLoss({
        courseConfirmation,
        namedStudents,
        selectedPrograms,
        programResponses,
        isDuplicate: !!duplicateFromId,
        hasCourse: !!course,
    });

    // A section record owns its section and CRN. Whatever filled them in
    // (a duplicate, the course just picked) gives way to it once that
    // record has loaded.
    const courseRecordId = course?._id;
    const recordSection = course?.section;
    const recordCrn = course?.crn;
    useEffect(() => {
        if (!takesRecordRef.current) return;
        if (!courseRecordId || courseRecordId !== courseId) return;
        const owned = sectionOfRecord({
            section: recordSection,
            crn: recordCrn,
        });
        setCourseConfirmation((prev) =>
            Object.entries(owned).every(([k, v]) => prev[k] === v)
                ? prev
                : { ...prev, ...owned }
        );
    }, [
        courseRecordId,
        courseId,
        recordSection,
        recordCrn,
        courseConfirmation,
    ]);

    // Loading the new course remounts Section 1, which then puts focus back
    // on Change course
    const refocusChangeCourseRef = useRef(false);

    const handleCourseChange = useCallback(
        (selected) => {
            // Picking the course already loaded changes nothing
            if (selected._id === courseId) return;
            refocusChangeCourseRef.current = true;
            freshStartRef.current = false;
            takesRecordRef.current = true;
            // A catalog record that is one section carries that section's
            // number and CRN, so they come from the record the request is
            // linked to and cannot disagree with it
            const pickedSection = sectionOfRecord(selected);
            // A duplicate moving to another section of the same course keeps
            // the modality and the kinds of meetings it copied
            const keepsKinds =
                !!duplicateFromId && isSiblingSection(course, selected);
            if (courseLoss.sectionDetails) {
                setCourseConfirmation((prev) => ({
                    ...prev,
                    crn: '',
                    enrollment: '',
                    sectionNumber: '',
                    partOfTermCode: '',
                    partOfTermName: '',
                    ...(keepsKinds
                        ? { meetings: meetingTypesOnly(prev.meetings) }
                        : {
                              modality: '',
                              otherModality: '',
                              meetings: [emptyMeeting()],
                          }),
                    ...pickedSection,
                }));
            } else if (pickedSection.crn || pickedSection.sectionNumber) {
                setCourseConfirmation((prev) => ({
                    ...prev,
                    ...pickedSection,
                }));
            }
            setCrnCheckResult(null);
            setUnlistedForm(null);
            if (courseLoss.namedStudents) setNamedStudents([]);
            // In this same update, so nothing checks programs against the
            // course just left
            setEligiblePrograms(null);
            // A duplicate keeps its copied answers on purpose. Programs the
            // last course couldn't take go back in, and the effect below
            // checks them all against the new course.
            if (duplicateFromId) {
                if (prunedPrograms.length > 0) {
                    setSelectedPrograms((prev) =>
                        [
                            ...prev,
                            ...prunedPrograms.filter(
                                (p) => !prev.some((q) => q.key === p.key)
                            ),
                        ].map((p, i) => ({ ...p, rank: i + 1 }))
                    );
                }
                setPrunedPrograms([]);
                // Who the LA nominee is went with the named students
                if (courseLoss.nominee) {
                    setProgramResponses((prev) => ({
                        ...prev,
                        la: {
                            ...prev.la,
                            nomineeStatus: null,
                            nomineeDeclinePreference: '',
                        },
                    }));
                }
                setSearchParams(wizardParams(selected._id));
                return;
            }
            // namedStudents and programResponses are keyed by program type
            // (forProgram: 'ta', programResponses.la...), not by course, so
            // clearing selectedPrograms alone doesn't stop them from coming
            // back: re-selecting the same program for the new course would
            // otherwise resurrect a named student or an LA/TA/SI answer
            // (requestedLACount, weeklyMeetingHours, ...) that was actually
            // entered thinking about the course just left.
            if (courseLoss.programs) setSelectedPrograms([]);
            if (courseLoss.programAnswers) {
                setProgramResponses(getDefaultProgramResponses());
            }
            setSearchParams(wizardParams(selected._id));
        },
        // wizardParams reads only refs and duplicateFromId
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            setSearchParams,
            duplicateFromId,
            prunedPrograms,
            courseId,
            course,
            courseLoss,
        ]
    );

    // Section 6: General Acknowledgments
    const [acknowledgesGeneralTerms, setAcknowledgesGeneralTerms] =
        useState(false);

    // Section 7: Review and Submit
    const [confirmsSubmissionAccuracy, setConfirmsSubmissionAccuracy] =
        useState(false);
    // Distinguishes "never confirmed yet" from "was confirmed, then
    // invalidated by an edit" — only the latter gets an explanatory note
    // on Section 7 (see confirmationWasInvalidated below), so a fresh
    // application doesn't show a "your confirmation was cleared" message
    // that was never true. Mirrors confirmsSubmissionAccuracy in a ref so
    // the invalidation effect below can read the latest value without
    // adding it as its own dependency — that would make checking the box
    // re-trigger the effect and immediately uncheck itself.
    const [confirmationWasInvalidated, setConfirmationWasInvalidated] =
        useState(false);
    const confirmsSubmissionAccuracyRef = useRef(false);
    const skipFirstInvalidationRunRef = useRef(true);

    const handleConfirmsSubmissionAccuracyChange = (checked) => {
        setConfirmsSubmissionAccuracy(checked);
        confirmsSubmissionAccuracyRef.current = checked;
        if (checked) setConfirmationWasInvalidated(false);
    };

    const hasTA = selectedPrograms.some((p) => p.key === 'ta');
    const hasLA = selectedPrograms.some((p) => p.key === 'la');
    const laRequirements = useLARequirements(currentTermCode, hasLA);

    // Editing an answer Review & Submit shows, after confirming it, clears
    // the confirmation. skipFirstInvalidationRunRef skips the
    // mount-triggered run.
    const confirmedAnswers = getConfirmedAnswers({
        courseId: course?._id,
        isSubmittedOnBehalf,
        courseConfirmation,
        selectedPrograms,
        courseContext,
        namedStudents,
        programResponses,
    });
    useEffect(() => {
        if (skipFirstInvalidationRunRef.current) {
            skipFirstInvalidationRunRef.current = false;
            return;
        }
        if (confirmsSubmissionAccuracyRef.current) {
            setConfirmationWasInvalidated(true);
            confirmsSubmissionAccuracyRef.current = false;
        }
        setConfirmsSubmissionAccuracy(false);
        // A fixed-length list from getConfirmedAnswers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, confirmedAnswers);

    // Load the application being duplicated. With no course picked yet,
    // find this term's copy of its course (or fall back to the unlisted
    // form) and put it in the URL so the course effect below loads it.
    const duplicateSourceId = duplicateSource?._id;
    useEffect(() => {
        if (!duplicateFromId || termsLoading || !currentTermCode) return;
        // Already loaded this one
        if (duplicateSourceId === duplicateFromId) return;
        if (duplicateSourceId) setDuplicateSource(null);
        setDuplicateError(null);
        let cancelled = false;
        const loadDuplicateSource = async () => {
            try {
                const app =
                    await applicationService.getApplicationById(
                        duplicateFromId
                    );
                if (cancelled) return;
                if (!courseId) {
                    const matchId = await findCourseForTerm(
                        app.course,
                        currentTermCode
                    );
                    if (cancelled) return;
                    setSearchParams(
                        {
                            course: matchId || 'unlisted',
                            duplicateFrom: duplicateFromId,
                        },
                        { replace: true }
                    );
                }
                // Last, since setting it re-runs this effect
                setDuplicateSource(app);
            } catch {
                if (cancelled) return;
                setDuplicateError(
                    'Failed to load the request to duplicate. Please try again.'
                );
            }
        };
        loadDuplicateSource();
        return () => {
            cancelled = true;
        };
        // courseId changing is this effect's own doing, and currentTermCode
        // is settled once termsLoading is false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duplicateFromId, termsLoading, duplicateSourceId]);

    // Fill the wizard from the source once. An on-behalf source also waits
    // for the profile, which decides whether "on behalf" carries over.
    useEffect(() => {
        if (!duplicateSource) return;
        if (duplicateSource.isSubmittedOnBehalf && !userProfile) return;
        if (seededIdRef.current === duplicateSource._id) return;
        seededIdRef.current = duplicateSource._id;
        const seed = getDuplicateSeed(duplicateSource, userProfile?._id);
        setIsSubmittedOnBehalf(seed.isSubmittedOnBehalf);
        setCourseConfirmation(seed.courseConfirmation);
        setCourseContext(seed.courseContext);
        setProgramResponses(seed.programResponses);
        setSelectedPrograms(seed.selectedPrograms);
        setPrunedPrograms([]);
    }, [duplicateSource, userProfile]);

    // Take out copied programs the course isn't eligible for, or that are
    // closed this term. Faculty can only pick open, eligible programs
    // themselves, so this only ever removes copied ones.
    const applicationWindows = currentTerm?.applicationWindows;
    useEffect(() => {
        if (!duplicateFromId) return;
        const kept = keepEligiblePrograms(
            selectedPrograms,
            eligiblePrograms,
            applicationWindows
        );
        if (kept === selectedPrograms) return;
        const dropped = selectedPrograms.filter(
            (p) => !kept.some((k) => k.key === p.key)
        );
        setPrunedPrograms((prev) => [
            ...prev,
            ...dropped.filter((p) => !prev.some((q) => q.key === p.key)),
        ]);
        setSelectedPrograms(kept);
    }, [
        duplicateFromId,
        eligiblePrograms,
        applicationWindows,
        selectedPrograms,
    ]);

    useEffect(() => {
        if (!courseId) {
            // Still looking up the duplicate's or the draft's course
            if (duplicateFromId || resumeId) return;
            setError('No course selected. Please select a course first.');
            setLoading(false);
            return;
        }
        // No real course exists yet — Section 1's own unlisted-course form
        // collects one, and handleCourseChange re-runs this effect once it
        // does (the ?course= param becomes a real id). Neither fetch below
        // has anything to ask for in the meantime.
        if (isUnlisted) {
            setCourse(null);
            setEligiblePrograms(null);
            setUnavailableReasons({});
            setError(null);
            setLoading(false);
            return;
        }

        // Neither call depends on the other's result — fire them together
        // instead of one after the other. Each is its own chain (not a
        // shared await) so loading clears as soon as the course settles,
        // same as before, instead of waiting on whichever of the two
        // happens to be slower; the eligibility .catch() also covers a
        // malformed response body throwing in .then(), not just the
        // request itself rejecting. Neither chain is awaited — both fully
        // handle their own success/failure via setState, and nothing here
        // depends on either finishing first.
        setLoading(true);
        setError(null);
        // A late answer for the course just left must not land on this one
        let active = true;
        // Until this course's answer arrives, so a duplicate isn't checked
        // against the last course's eligibility
        setEligiblePrograms(null);

        courseService
            .getCourseById(courseId)
            .then((data) => {
                if (!active) return;
                setCourse(data);
                // A request started from a section's own record begins on
                // that section. A draft or a duplicate already has its own.
                if (freshStartRef.current) {
                    freshStartRef.current = false;
                    const pickedSection = sectionOfRecord(data);
                    setCourseConfirmation((prev) =>
                        prev.crn || prev.sectionNumber
                            ? prev
                            : { ...prev, ...pickedSection }
                    );
                }
            })
            .catch(() => {
                if (!active) return;
                // Must clear `course`, not just set `error` — the full-page
                // error guard below only fires on `error && !course`, and
                // leaving a stale `course` from before a course change would
                // let the user submit courseConfirmation data (CRN, section,
                // etc.) they just filled in for a DIFFERENT course than the
                // one that ends up on the application.
                setCourse(null);
                setError('Failed to load course details.');
            })
            .finally(() => active && setLoading(false));

        programService
            .getEligiblePrograms(courseId)
            .then((response) => {
                if (!active) return;
                const eligibleProgramTypes = response.eligiblePrograms.map(
                    (program) => program.programType
                );
                setEligiblePrograms(eligibleProgramTypes);
                setUnavailableReasons(subjectUnavailableReasons(response));
            })
            .catch(() => {
                if (!active) return;
                setEligiblePrograms(null);
                setUnavailableReasons({});
            });
        return () => {
            active = false;
        };
    }, [courseId, isUnlisted, duplicateFromId, resumeId, courseAttempt]);

    // Load a saved draft into the wizard. Its CRN is checked here rather than
    // by Section 1, which only runs the check while it is on screen, so the
    // section to resume at is decided with the check's answer.
    useEffect(() => {
        if (!resumeId) return undefined;
        let cancelled = false;
        const loadDraft = async () => {
            try {
                const app =
                    await applicationService.getApplicationById(resumeId);
                if (cancelled) return;
                if (app.status !== 'draft') {
                    navigate(`/applications/${resumeId}`, { replace: true });
                    return;
                }
                const seed = getResumeSeed(app);
                setIsSubmittedOnBehalf(seed.isSubmittedOnBehalf);
                setCourseConfirmation(seed.courseConfirmation);
                setCourseContext(seed.courseContext);
                setProgramResponses(seed.programResponses);
                setSelectedPrograms(seed.selectedPrograms);
                setNamedStudents(seed.namedStudents);
                setAcknowledgesGeneralTerms(seed.acknowledgesGeneralTerms);
                const crn = seed.courseConfirmation.crn;
                if (CRN_PATTERN.test(crn)) {
                    try {
                        const result = await applicationService.checkCrn(
                            app.term,
                            crn,
                            resumeId
                        );
                        if (cancelled) return;
                        setCrnCheckResult(result);
                    } catch {
                        // Treated as no conflict, as Section 1 does; submit
                        // is still refused on a real one
                    }
                }
                setResumeDraft(app);
                setSearchParams(
                    {
                        // A draft whose course was removed asks for one again
                        course: app.course?._id || app.course || 'unlisted',
                        draft: resumeId,
                    },
                    { replace: true }
                );
                setResumeReady(true);
            } catch {
                if (!cancelled) {
                    setResumeError(
                        'Your saved draft could not be loaded. Please try again.'
                    );
                }
            }
        };
        loadDraft();
        return () => {
            cancelled = true;
        };
        // Runs once for the draft named when the page opened
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeId]);

    const toggleProgram = useCallback((program) => {
        setSelectedPrograms((prev) => {
            const exists = prev.find((p) => p.key === program.key);
            if (exists) {
                // Remove and re-rank
                const filtered = prev.filter((p) => p.key !== program.key);
                return filtered.map((p, i) => ({ ...p, rank: i + 1 }));
            }
            return [...prev, { ...program, rank: prev.length + 1 }];
        });
    }, []);

    const moveProgram = useCallback((index, direction) => {
        setSelectedPrograms((prev) => {
            const arr = [...prev];
            const swapIndex = index + direction;
            if (swapIndex < 0 || swapIndex >= arr.length) return prev;
            [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
            return arr.map((p, i) => ({ ...p, rank: i + 1 }));
        });
    }, []);

    const addNamedStudent = useCallback((forProgram = 'ta') => {
        setNamedStudents((prev) => [
            ...prev,
            { name: '', email: '', studentId: '', forProgram },
        ]);
    }, []);

    const updateNamedStudent = useCallback((index, field, value) => {
        setNamedStudents((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    const removeNamedStudent = useCallback((index) => {
        setNamedStudents((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const updateProgramResponse = useCallback((programKey, field, value) => {
        setProgramResponses((prev) => ({
            ...prev,
            [programKey]: { ...prev[programKey], [field]: value },
        }));
    }, []);

    // Clear the named-instructor fields when switching back to "I am the
    // instructor of record" — otherwise a name/email typed in, then
    // abandoned by flipping the radio back, still gets submitted
    const handleIsSubmittedOnBehalfChange = useCallback((value) => {
        setIsSubmittedOnBehalf(value);
        if (!value) {
            setCourseConfirmation((prev) => ({
                ...prev,
                instructorOfRecordName: '',
                instructorOfRecordEmail: '',
            }));
        }
    }, []);

    // One entry per wizard step, in display order. The section files are
    // numbered to match their position here, so reordering this array means
    // renumbering the files too. Acknowledgments & Contract is LA-specific
    // and filtered out entirely below when LA isn't selected.
    //
    // `errors` is precomputed here (allSections is rebuilt every render, so
    // this always reflects the latest state) instead of a canProceed()
    // function — one array answers both "can this step proceed"
    // (errors.length === 0) and "what should the user be told is missing,"
    // so the two checks can't drift apart the way two separate ones could.
    // getEligibilityCourseErrors has no way to know whether a course was
    // ever actually chosen or created (isUnlisted/course=unlisted arriving
    // with nothing picked yet, or the in-page unlisted-course form
    // abandoned mid-fill) — course is CreateApplication.js's own state, not
    // part of courseConfirmation. Without this, submit would go ahead with
    // course still null and throw on course._id.
    const eligibilityCourseErrors = [
        ...(!course || pickingNewCourse
            ? [{ id: 'ca-unlisted-subject', label: 'Course' }]
            : []),
        ...getEligibilityCourseErrors(
            courseConfirmation,
            isSubmittedOnBehalf,
            crnBlocksSubmit(crnCheckResult) || crnChecking
        ),
    ];
    const programSelectionErrors = getProgramSelectionErrors(selectedPrograms);
    const acknowledgementContractErrors = getAcknowledgementContractErrors(
        programResponses.la,
        'ca',
        !!laRequirements.requirements,
        laRequirements.noTerm
    );
    const courseContextErrors = getCourseContextErrors(courseContext);
    const programQuestionnaireErrors = getProgramQuestionnaireErrors(
        namedStudents,
        programResponses,
        selectedPrograms
    );
    const generalAcknowledgmentErrors = getGeneralAcknowledgmentErrors(
        acknowledgesGeneralTerms,
        'ca'
    );
    const reviewConfirmationErrors = getReviewConfirmationErrors(
        confirmsSubmissionAccuracy,
        'ca'
    );
    // Field errors only render once the section has been left or a submit
    // was tried — see leftSections above
    const showsErrors = (section) =>
        submitAttempted || leftSections.has(section.label);

    // What gets submitted. Built once so Review & Submit shows exactly this.
    const relevantNamedStudents = getRelevantNamedStudents(
        namedStudents,
        selectedPrograms
    );
    // Only the selected programs' answers
    const specificResponses = {};
    selectedPrograms.forEach((p) => {
        if (programResponses[p.key]) {
            specificResponses[p.key] = programResponses[p.key];
        }
    });
    // A resumed draft keeps the term it was started in
    const applicationTerm = resumeDraft?.term ?? currentTermCode;
    // The section's part of term starts at the term's default, so a request
    // for a full-semester section needs no extra step. Also refills it after
    // a course change clears the section's details.
    const partsOfTerm = usePartsOfTerm(applicationTerm);
    useEffect(() => {
        if (!partsOfTerm || courseConfirmation.partOfTermCode) return;
        const { code, name } = defaultPartOfTerm(partsOfTerm);
        setCourseConfirmation((prev) =>
            prev.partOfTermCode
                ? prev
                : { ...prev, partOfTermCode: code, partOfTermName: name }
        );
    }, [partsOfTerm, courseConfirmation.partOfTermCode]);
    const applicationData = {
        courseId: course?._id,
        term: applicationTerm,
        programPreferences: toProgramPreferences(selectedPrograms),
        courseConfirmation,
        isSubmittedOnBehalf,
        courseContext,
        acknowledgesGeneralTerms,
        confirmsSubmissionAccuracy,
        ...(relevantNamedStudents.length > 0 && {
            namedStudents: relevantNamedStudents,
        }),
        ...(Object.keys(specificResponses).length > 0 && {
            programSpecificResponses: specificResponses,
        }),
    };

    const laNomineeIds = namedStudents
        .filter((s) => s.forProgram === 'la')
        .map((s) => s.studentId);
    // A refused ID's message stays only while its row still holds that ID
    const nomineeIdErrors = new Map(
        (nomineeIdRefusal?.refused ?? [])
            .filter(
                ({ position, studentId }) =>
                    laNomineeIds[position] === studentId
            )
            .map(({ position }) => [
                nomineeIdFieldFor(position),
                nomineeIdRefusal.message,
            ])
    );

    const allSections = [
        {
            label: 'Eligibility & Course',
            errors: eligibilityCourseErrors,
            render: (errorIds) => (
                <CreateApplicationSection1
                    value={courseConfirmation}
                    onChange={(patch) =>
                        setCourseConfirmation((prev) => ({
                            ...prev,
                            ...patch,
                        }))
                    }
                    course={course}
                    onCourseChange={handleCourseChange}
                    courseChangeLoss={courseLoss}
                    isDuplicate={!!duplicateFromId}
                    refocusChangeCourseRef={refocusChangeCourseRef}
                    termCode={applicationTerm}
                    excludeApplicationId={savedId ?? undefined}
                    isSubmittedOnBehalf={isSubmittedOnBehalf}
                    onIsSubmittedOnBehalfChange={
                        handleIsSubmittedOnBehalfChange
                    }
                    crnCheckResult={crnCheckResult}
                    rankedProgramKeys={selectedPrograms.map((p) => p.key)}
                    onCrnCheckResult={handleCrnCheckResult}
                    onCrnCheckingChange={handleCrnCheckingChange}
                    partsOfTerm={partsOfTerm}
                    partOfTermRefusal={partOfTermRefusal}
                    onCoursePickerModeChange={(mode) =>
                        setPickingNewCourse(mode !== 'display')
                    }
                    unlistedDefaults={duplicateSource?.course}
                    unlistedForm={unlistedForm}
                    onUnlistedFormChange={setUnlistedForm}
                    errorIds={errorIds}
                />
            ),
        },
        {
            label: 'Select Programs',
            errors: programSelectionErrors,
            render: (errorIds) => (
                <CreateApplicationSection2
                    selectedPrograms={selectedPrograms}
                    toggleProgram={toggleProgram}
                    moveProgram={moveProgram}
                    eligiblePrograms={eligiblePrograms}
                    unavailableReasons={unavailableReasons}
                    currentTerm={currentTerm}
                    userProfile={userProfile}
                    hasTA={hasTA}
                    errorIds={errorIds}
                />
            ),
        },
        {
            label: 'Acknowledgments & Contract',
            laOnly: true,
            errors: acknowledgementContractErrors,
            render: (errorIds) => (
                <CreateApplicationSection3
                    la={programResponses.la}
                    onChange={(field, checked) =>
                        updateProgramResponse('la', field, checked)
                    }
                    requirements={laRequirements}
                    errorIds={errorIds}
                />
            ),
        },
        {
            label: 'Course Context',
            errors: courseContextErrors,
            render: (errorIds) => (
                <CreateApplicationSection4
                    value={courseContext}
                    onChange={(patch) =>
                        setCourseContext((prev) => ({ ...prev, ...patch }))
                    }
                    errorIds={errorIds}
                />
            ),
        },
        {
            label: 'Program Details',
            holdsNomineeId: true,
            errors: programQuestionnaireErrors,
            render: (errorIds) => (
                <CreateApplicationSection5
                    namedStudents={namedStudents}
                    addNamedStudent={addNamedStudent}
                    updateNamedStudent={updateNamedStudent}
                    removeNamedStudent={removeNamedStudent}
                    selectedPrograms={selectedPrograms}
                    programResponses={programResponses}
                    onProgramResponseChange={updateProgramResponse}
                    hasTA={hasTA}
                    courseConfirmation={courseConfirmation}
                    laTrainingSessions={
                        laRequirements.requirements?.trainingSessions
                    }
                    errorIds={errorIds}
                    nomineeIdErrors={nomineeIdErrors}
                />
            ),
        },
        {
            label: 'General Acknowledgments',
            errors: generalAcknowledgmentErrors,
            render: (errorIds) => (
                <CreateApplicationSection6
                    acknowledgesGeneralTerms={acknowledgesGeneralTerms}
                    onChange={setAcknowledgesGeneralTerms}
                    term={currentTerm}
                    programs={selectedPrograms}
                    idPrefix="ca"
                    errorIds={errorIds}
                />
            ),
        },
        {
            label: 'Review & Submit',
            errors: reviewConfirmationErrors,
            render: (errorIds) => (
                <CreateApplicationSection7
                    application={{
                        ...applicationData,
                        course,
                        status: 'draft',
                    }}
                    confirmsSubmissionAccuracy={confirmsSubmissionAccuracy}
                    wasInvalidated={confirmationWasInvalidated}
                    onChange={handleConfirmsSubmissionAccuracyChange}
                    idPrefix="ca"
                    errorIds={errorIds}
                    instructorMissing={
                        isSubmittedOnBehalf &&
                        (!courseConfirmation.instructorOfRecordName?.trim() ||
                            !courseConfirmation.instructorOfRecordEmail?.trim())
                    }
                />
            ),
        },
    ];

    const sections = allSections.filter((s) => !s.laOnly || hasLA);

    // Keeps `step` valid when hasLA toggles and the Acknowledgments & Contract
    // entry is added to or removed from `sections`. Only reachable
    // today while step === 2 (LA can only be toggled from Program
    // Selection), where this is always a no-op — it's a plain trailing
    // clamp, not a general reindex, so a future conditional section at a
    // different position would need more than this to stay correct.
    useEffect(() => {
        setStep((prev) => Math.min(prev, sections.length));
    }, [sections.length]);

    const currentSection = sections[step - 1];
    const currentErrors = currentSection?.errors ?? [];
    const showsCurrentErrors = !!currentSection && showsErrors(currentSection);
    const incompleteSections = sections.filter((s) => s.errors.length > 0);
    const otherIncompleteSections = incompleteSections.filter(
        (s) => s !== currentSection
    );
    const stepStatuses = sections.map((s) => {
        if (s.errors.length === 0) {
            return submitAttempted || reachedSections.has(s.label)
                ? 'complete'
                : 'untouched';
        }
        return showsErrors(s) ? 'incomplete' : 'untouched';
    });

    const stepTopRef = useRef(null);
    const stepContentRef = useRef(null);
    useStepChangeFocus(step, stepTopRef, stepContentRef);

    // A resumed draft opens at its first section still needing answers, by
    // the same errors submit checks, or at Review & Submit when none does.
    // Waits for everything those errors read: the course, the CRN check
    // (done while loading the draft), the part of term's default, and the LA
    // requirements when LA is ranked.
    const firstIncompleteStep =
        incompleteSections.length > 0
            ? sections.indexOf(incompleteSections[0]) + 1
            : sections.length;
    const resumeSettled =
        resumeReady &&
        !loading &&
        (!!course || isUnlisted) &&
        // A draft saved before the form asked for a part of term gets the
        // term's default first, so that alone does not reopen Section 1
        (!applicationTerm || !!courseConfirmation.partOfTermCode) &&
        !(hasLA && laRequirements.loading);
    useEffect(() => {
        if (positioned || !resumeSettled) return;
        setStep(firstIncompleteStep);
        setReachedSections(
            new Set(sections.slice(0, firstIncompleteStep).map((s) => s.label))
        );
        setPositioned(true);
    }, [positioned, resumeSettled, firstIncompleteStep, sections]);

    const currentLabel = currentSection?.label;
    useEffect(() => {
        if (!positioned || !currentLabel) return;
        setReachedSections((prev) =>
            prev.has(currentLabel) ? prev : new Set(prev).add(currentLabel)
        );
    }, [positioned, currentLabel]);

    // What a save of progress sends. Named students and program answers
    // always go, even when empty, so removing the last one is saved too (the
    // server leaves a field the request leaves out alone), and a named
    // student filled in part way goes as it is so it is there on resume.
    const draftPayload = {
        ...applicationData,
        namedStudents: getSavedNamedStudents(namedStudents, selectedPrograms),
        programSpecificResponses: specificResponses,
    };
    // What the save just before submit sends: what Review & Submit shows
    const submitPayload = {
        ...applicationData,
        namedStudents: relevantNamedStudents,
        programSpecificResponses: specificResponses,
    };
    // A draft is identified by its course; until one is chosen there is
    // nothing to file it under
    const canSaveDraft =
        !!course?._id && !pickingNewCourse && !!applicationTerm;
    const persistDraft = async (payload) => {
        if (savedIdRef.current) {
            await applicationService.updateApplication(
                savedIdRef.current,
                payload
            );
            return;
        }
        const created = await applicationService.createApplication(payload);
        savedIdRef.current = created._id;
        // A save finishing after the page was left must not pull the person
        // back to it through the URL
        if (!mountedRef.current) return;
        setSavedId(created._id);
        // savedHere tells CreateApplicationPage.js this is the same page
        // gaining its draft id, not a different draft to load
        setSearchParams(
            { course: payload.courseId, draft: created._id },
            { replace: true, state: { savedHere: true } }
        );
    };
    const { save, markClean, hasUnsavedChanges, saveState } = useSavedProgress({
        payload: draftPayload,
        persist: persistDraft,
        // Also stops the save on leaving, which would only be refused
        canSave: canSaveDraft && !alreadySubmitted,
    });
    // The first save creates the draft even when only the course is chosen
    const saveDraft = () => save({ force: !savedIdRef.current });

    // What the form holds when it first appears (defaults, a copied
    // application, or a loaded draft) is the baseline; only changes from it
    // count as unsaved
    const formShown =
        !loading &&
        !termsLoading &&
        positioned &&
        !(duplicateFromId && !duplicateSource);
    const baselineSetRef = useRef(false);
    useEffect(() => {
        if (!formShown || baselineSetRef.current) return;
        baselineSetRef.current = true;
        markClean();
    }, [formShown, markClean]);

    useEffect(() => {
        if (canSaveDraft) setFinishLaterNote(null);
    }, [canSaveDraft]);

    useLeaveGuard(
        !submitting &&
            !alreadySubmitted &&
            (hasUnsavedChanges || saveState.status === 'saving'),
        async (proceed) => {
            if (!canSaveDraft) {
                if (window.confirm(LEAVE_NO_COURSE_PROMPT)) proceed();
                return;
            }
            try {
                await saveDraft();
            } catch {
                if (window.confirm(LEAVE_UNSAVED_PROMPT)) proceed();
                return;
            }
            proceed();
        }
    );

    // On this route the server judges only the LA nominees' 900 numbers, so
    // a refusal naming studentId is about those fields. It names no row, so
    // it shows at each typed ID that is not nine digits, the rule the server
    // refused it by. True when shown.
    const showNomineeIdRefusal = (err) => {
        const { status, data } = err?.response ?? {};
        if (status !== 400 || data?.field !== 'studentId' || !data.message) {
            return false;
        }
        const refused = laNomineeIds
            .map((studentId, position) => ({ studentId, position }))
            .filter(
                ({ studentId }) =>
                    studentId?.trim() && !NINE_DIGITS.test(studentId.trim())
            );
        if (refused.length === 0) return false;
        setNomineeIdRefusal({ refused, message: data.message });
        setFocusRequest({ id: nomineeIdFieldFor(refused[0].position) });
        return true;
    };

    // The server judges a part of term against the term's list. A refusal
    // takes the faculty member back to the field with the reason. True when
    // shown.
    const showPartOfTermRefusal = (err) => {
        const { status, data } = err?.response ?? {};
        if (status !== 400 || data?.field !== 'partOfTerm' || !data.message) {
            return false;
        }
        const { partOfTermCode: code, partOfTermName: name } =
            courseConfirmation;
        setPartOfTermRefusal({ code, name, message: data.message });
        setStep(1);
        setFocusRequest({
            id:
                code === OTHER_PART_OF_TERM
                    ? 'ca-other-part-of-term'
                    : 'ca-part-of-term',
        });
        return true;
    };

    const saveAndFinishLater = async () => {
        // Nothing can be saved (and a save with no changes would resolve and
        // claim the draft was saved), so go where the alert points
        if (alreadySubmitted) {
            navigate('/applications', {
                state: { success: ALREADY_SUBMITTED_LEFT_MESSAGE },
            });
            return;
        }
        if (!canSaveDraft) {
            setFinishLaterNote(NO_COURSE_YET_NOTE);
            return;
        }
        let result;
        try {
            result = await saveDraft();
        } catch (err) {
            // The status line says it failed and offers to try again
            showNomineeIdRefusal(err);
            showPartOfTermRefusal(err);
            return;
        }
        if (result?.skipped) {
            setFinishLaterNote(NOT_SAVED_PREVIEWING_NOTE);
            return;
        }
        navigate('/applications', {
            state: { success: DRAFT_SAVED_MESSAGE, tab: 'drafts' },
        });
    };

    // Previous, Next and the section pills all move freely (#463). Leaving
    // a section is what makes its missing fields show up later.
    const goToStep = async (target) => {
        // Clicking the pill you're on isn't leaving it
        if (target === step) return;
        if (currentSection) {
            setLeftSections((prev) => new Set(prev).add(currentSection.label));
        }
        // Leaving a section is when progress is saved (#519)
        const saving = canSaveDraft ? saveDraft() : Promise.resolve();
        // Only the server judges a nominee's 900 number, so leaving the
        // section that holds one waits for the save: a refusal keeps the
        // section open with the message at the field
        if (
            currentSection?.holdsNomineeId &&
            laNomineeIds.some((id) => id?.trim())
        ) {
            try {
                await saving;
            } catch (err) {
                if (showNomineeIdRefusal(err)) return;
            }
        } else {
            saving.catch((err) => {
                showPartOfTermRefusal(err);
            });
        }
        setStep(target);
    };

    // Moving freely means reaching Review & Submit no longer proves the
    // earlier sections are done, so submit checks all of them. Still never
    // disabled (#384): it jumps to the first incomplete section, focuses
    // the first missing field and the summary names what's missing.
    const attemptSubmit = () => {
        if (incompleteSections.length === 0) {
            handleSubmit();
            return;
        }
        setSubmitAttempted(true);
        const first = incompleteSections[0];
        setStep(sections.indexOf(first) + 1);
        setFocusRequest({ id: first.errors[0].id });
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            setSubmitError(null);
            setSubmitHolder(null);

            // Saved first, so a refused submit leaves the work in the draft
            const result = await saveAndSubmit({
                save: (payload) => save({ force: true, payload }),
                payload: submitPayload,
                getId: () => savedIdRef.current,
            });
            if (result.submitted) {
                markClean();
                navigate('/applications', {
                    state: {
                        success: 'Support request submitted successfully!',
                    },
                });
                return;
            }
            if (showPartOfTermRefusal(result.error)) return;
            setSubmitError(result.message);
            if (result.alreadySubmitted) setAlreadySubmitted(true);
            // A 409 means the CRN was claimed meanwhile (another tab, or
            // another of this applicant's drafts submitted first). Refresh
            // the check so Section 1 and its step pill show the block too,
            // rather than still describing an informational own draft.
            const crn = courseConfirmation.crn;
            if (
                result.error?.response?.status === 409 &&
                CRN_PATTERN.test(crn ?? '')
            ) {
                const holder = result.error.response.data?.holder;
                if (holder) {
                    setSubmitHolder(holder);
                    setCrnCheckResult({ exists: true, own: false, holder });
                } else {
                    applicationService
                        .checkCrn(applicationTerm, crn, savedIdRef.current)
                        .then(setCrnCheckResult)
                        .catch(() => {});
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Nothing to duplicate into without an open term (same wording as the
    // Start page)
    if (duplicateFromId && !termsLoading && !currentTermCode) {
        return (
            <Container className="py-5">
                <Alert variant="warning">
                    No term is open for support requests yet. Check back once
                    the request window has been announced.
                </Alert>
                <Link to="/applications">Back to Support requests</Link>
            </Container>
        );
    }

    // Stops here rather than showing an empty wizard, since duplicating was
    // the point
    if (duplicateError) {
        return (
            <Container className="py-5">
                <Alert variant="danger">{duplicateError}</Alert>
                <Link to="/applications">Back to Support requests</Link>
            </Container>
        );
    }

    if (resumeError) {
        return (
            <Container className="py-5">
                <Alert variant="danger">{resumeError}</Alert>
                <Link to="/applications">Back to Support requests</Link>
            </Container>
        );
    }

    // The course failed to load (deleted course, network error): stop here
    // instead of rendering a wizard that will crash on course._id at submit.
    // Checked before the loading screen, which a resumed draft waits on until
    // its course is in, so a failure would otherwise spin forever.
    if (courseId && !isUnlisted && !loading && error && !course) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <p>{error}</p>
                    <Button
                        variant="outline-danger"
                        onClick={() => setCourseAttempt((n) => n + 1)}
                    >
                        Try again
                    </Button>
                </Alert>
                {resumeId ? (
                    <Link to="/applications">Back to Support requests</Link>
                ) : (
                    <Link to="/courses">Browse courses</Link>
                )}
            </Container>
        );
    }

    // Wait for the source too, so it can't overwrite what someone has
    // already started typing, and for a resumed draft to be in place
    if (
        loading ||
        termsLoading ||
        (duplicateFromId && !duplicateSource) ||
        !positioned
    ) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading course details...</p>
            </Container>
        );
    }

    if (!courseId) {
        return (
            <Container className="py-5">
                <Alert variant="warning">
                    No course selected.{' '}
                    <Link to="/courses">Browse courses</Link> to find one to
                    apply for.
                </Alert>
            </Container>
        );
    }

    // A duplicate's notice asks only for what the faculty member can enter:
    // a section record fills in its own CRN and section number
    const setByRecord = [
        ...(course?.crn ? ['CRN'] : []),
        ...(course?.section ? ['section number'] : []),
    ];
    const toEnter = [
        ...(course?.crn ? [] : ['CRN']),
        ...(course?.section ? [] : ['section number']),
        'enrollment',
        'meeting days and times',
    ];

    return (
        <Container className="py-4">
            {/* Header */}
            <div className="mb-4">
                <Button
                    variant="link"
                    as={Link}
                    to={backLink.to}
                    className="ps-0 mb-2"
                >
                    &larr; Back to {backLink.label}
                </Button>
                <h1>Request Support</h1>
                {course && (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="d-flex align-items-center gap-3">
                            <Badge bg="secondary" className="fs-6 px-3 py-2">
                                {course.subject} {course.courseNumber}
                            </Badge>
                            {/* Instructor, section and schedule are the
                                applicant's own section details, entered in
                                Eligibility & Course below, not catalog
                                fields — the catalog (#381) no longer shows
                                them either. */}
                            <p className="mb-0 fw-semibold">{course.title}</p>
                        </Card.Body>
                    </Card>
                )}
            </div>

            <GuideLink
                audience="faculty"
                className="d-block small mb-3"
                short
            />

            <ApplicationDeadlineCard
                term={currentTerm}
                facultyEmail={userProfile?.email}
            />

            {duplicateSource && (
                <Alert variant="info">
                    <p className="mb-1">
                        Copied from the{' '}
                        <strong>
                            {duplicateSource.course
                                ? `${duplicateSource.course.subject} ${duplicateSource.course.courseNumber}`
                                : 'earlier'}
                            {duplicateSource.courseConfirmation?.sectionNumber
                                ? ` section ${duplicateSource.courseConfirmation.sectionNumber}`
                                : ''}
                        </strong>{' '}
                        application.{' '}
                        {setByRecord.length > 0 &&
                            `The section below sets its ${joinWithAnd(setByRecord)}. `}
                        Enter this section&apos;s {joinWithAnd(toEnter)}, and
                        name any students again. Acknowledgments need to be
                        checked again too.
                    </p>
                    {prunedPrograms.length > 0 && (
                        <p className="mb-1">
                            {prunedPrograms.map((p) => p.label).join(' and ')}{' '}
                            {prunedPrograms.length === 1 ? "isn't" : "aren't"}{' '}
                            available for this course this term, so{' '}
                            {prunedPrograms.length === 1
                                ? 'it was'
                                : 'they were'}{' '}
                            not copied.
                        </p>
                    )}
                    {/* Only a duplicate whose course this term's catalog
                        didn't have lands on course=unlisted */}
                    {isUnlisted && (
                        <p className="mb-0">
                            That course isn&apos;t in this term&apos;s catalog
                            yet. Tell us about it below.
                        </p>
                    )}
                </Alert>
            )}

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* A draft whose term or program has since closed is still
                shown, with the reason it can't be submitted (#519) */}
            {resumeDraft?.submitBlocker && (
                <Alert variant="warning">
                    <p className="mb-1 fw-semibold">
                        This draft can&apos;t be submitted as it stands.
                    </p>
                    <p className="mb-0">{resumeDraft.submitBlocker}</p>
                </Alert>
            )}

            <WizardStepIndicator
                ref={stepTopRef}
                total={sections.length}
                current={step}
                labels={sections.map((s) => s.label)}
                statuses={stepStatuses}
                onSelect={goToStep}
            />

            {/* Section Content */}
            <Card ref={stepContentRef} className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    {currentSection?.render(
                        showsCurrentErrors
                            ? new Set(currentErrors.map((e) => e.id))
                            : new Set()
                    )}
                </Card.Body>
            </Card>

            {submitError && (
                <Alert
                    ref={submitErrorRef}
                    variant="danger"
                    dismissible
                    onClose={() => setSubmitError(null)}
                    tabIndex={-1}
                    className="mb-3"
                >
                    {submitError}
                    {submitHolder && (
                        <p className="mb-0 mt-2">
                            <CrnHolderNotice
                                holder={submitHolder}
                                crn={courseConfirmation.crn}
                                programKeys={selectedPrograms.map((p) => p.key)}
                            />
                        </p>
                    )}
                </Alert>
            )}

            {/* Navigation */}
            <ActionBar
                label="Request steps"
                primary={
                    step < sections.length ? (
                        <Button
                            variant="primary"
                            onClick={() => goToStep(step + 1)}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="success"
                            onClick={attemptSubmit}
                            disabled={submitting || previewLocked}
                            aria-describedby={
                                previewLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                            }
                        >
                            {submitting ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Submitting...
                                </>
                            ) : (
                                'Submit request'
                            )}
                        </Button>
                    )
                }
                secondary={[
                    <Button
                        key="previous"
                        variant="outline-primary"
                        onClick={() => goToStep(step - 1)}
                        disabled={step === 1}
                    >
                        Previous
                    </Button>,
                    // Never disabled, like Submit (#384): with no course
                    // chosen yet it says why nothing can be saved
                    <Button
                        key="save"
                        variant="outline-primary"
                        onClick={saveAndFinishLater}
                    >
                        Save and finish later
                    </Button>,
                ]}
            />
            {/* Always mounted so the live region is already in the
                accessibility tree before its text changes — a region that
                only mounts once the text is ready to announce is unreliable
                across screen readers. Names the specific fields still
                missing instead of a generic "form incomplete" message
                (#384), and after a submit attempt which other sections
                still need work (#463). */}
            <small className="d-block text-danger mt-2" aria-live="polite">
                {[
                    showsCurrentErrors &&
                        currentErrors.length > 0 &&
                        `Missing: ${currentErrors.map((e) => e.label).join(', ')}.`,
                    submitAttempted &&
                        otherIncompleteSections.length > 0 &&
                        `${currentErrors.length > 0 ? 'Also incomplete' : 'Still incomplete'}: ${otherIncompleteSections.map((s) => s.label).join(', ')}.`,
                ]
                    .filter(Boolean)
                    .join(' ')}
            </small>
            <PreviewLockNote locked={previewLocked} />
            <div className="mt-2">
                <SaveProgressStatus
                    saveState={
                        alreadySubmitted ? { status: 'idle' } : saveState
                    }
                    onRetry={() => saveDraft().catch(() => {})}
                    note={
                        alreadySubmitted
                            ? ALREADY_SUBMITTED_SAVE_NOTE
                            : finishLaterNote
                    }
                    idleText={
                        savedId
                            ? 'Your progress is saved each time you move to another section.'
                            : 'Your request is saved as a draft once you move past a section.'
                    }
                />
            </div>
            <small className="d-block text-muted mt-1">
                Program coordinators can see that you&apos;ve started a request
                (not your answers) so they can help you finish.
            </small>
        </Container>
    );
}

export default CreateApplication;
